/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { z } from 'zod';

/**
 * A task, as GET /api/tasks actually returns it.
 *
 * Read off `getFullTask()` in server/routes/tasks.js rather than invented. Three
 * things about the shape that are easy to get wrong:
 *
 *  - **Dates are strings** (`YYYY-MM-DD`) or `''`, not timestamps. `created_at`,
 *    `updated_at` and `completed_at` are epoch milliseconds. Mixing the two is
 *    how a calendar ends up a day out.
 *  - **`sowNumber` is the only renamed field.** Everything else the browser sees
 *    keeps the column's own name, so `finish_date` really is snake_case here.
 *  - **`canEditFully` is authorisation, not a hint.** The server refuses the same
 *    four things regardless; this only lets the UI say so before the refusal.
 */

/**
 * **The columns are per circle now, and this is only the fallback.**
 *
 * `VERTICALS` used to be *the* list, imported by eleven components. A circle
 * chooses its own — they arrive on `GET /api/circles` and are read through
 * `useVerticals()` — and this stays as the answer for a circle that has never
 * been customised, which is what an empty `circle_verticals` means on the
 * server too. Both ends fall back to the same five, so an un-customised board is
 * bit-for-bit what it always was.
 *
 * `color` is the token, not a literal, so the columns, the modal's chips and the
 * calendar's tints can never disagree about what Discovery looks like.
 */
export const DEFAULT_VERTICALS = [
  { id: 'action', label: 'Action', color: 'action' },
  { id: 'discovery', label: 'Discovery', color: 'discovery' },
  { id: 'solutioning', label: 'Solutioning', color: 'solutioning' },
  { id: 'wip', label: 'WIP', color: 'wip' },
  { id: 'complete', label: 'Complete', color: 'complete' },
].map((v, i) => ({ ...v, position: i }));

/**
 * A column id is a **plain string** now, not a union of five literals.
 *
 * The union was doing real work — it caught a typo'd id at compile time — and
 * it cannot survive ids the server generates at runtime. What replaced it is the
 * runtime check that matters more: an id is only ever drawn if it is in the
 * circle's own column list, and the server refuses to store one that is not.
 */
export type VerticalId = string;

/**
 * **The done column, in every circle, always last.**
 *
 * Renameable — "Shipped", "Delivered", "Billed" — but never removable and never
 * moved, so this id is always a real column on every board. The board's
 * disclaimer, the calendar's completion tint and the archived view all rest on
 * that being true.
 */
export const DONE_VERTICAL = 'complete';

/** `var(--color-action)` from `action`. Empty falls back to a neutral. */
export function verticalColorVar(token: string | undefined): string {
  return token ? `var(--color-${token})` : 'var(--color-ink-faint)';
}

/**
 * P1 is the most urgent. **P4 is the default** for every task the server has not
 * been told about, so it is styled neutrally — a board full of P4 should not look
 * like a board full of alarms.
 */
export const PRIORITIES = [
  { id: 1, label: 'P1', name: 'Critical' },
  { id: 2, label: 'P2', name: 'High' },
  { id: 3, label: 'P3', name: 'Medium' },
  { id: 4, label: 'P4', name: 'Normal' },
  { id: 5, label: 'P5', name: 'Low' },
] as const;
export const DEFAULT_PRIORITY = 4;

export function priorityMeta(p: number | null | undefined) {
  return PRIORITIES.find((x) => x.id === Number(p)) ?? PRIORITIES[3];
}

/**
 * A person on a task is a **plain display name**, not an object.
 *
 * `server/owners.js` stores them as a JSON array of strings, and `decodeOwners`
 * hands back `string[]` — a legacy row holding one bare name decodes as a
 * single-element array. Emails are resolved separately into `ownerNotifications`
 * and are not part of this.
 *
 * This file first declared it as `{name, email}`, which made zod reject every
 * response and left the board empty with no cards and no obvious cause. That is
 * the boundary check doing its job: it failed at the point of disagreement
 * rather than producing `undefined` three components later.
 */
export const Owner = z.string();

export const Tag = z.object({
  id: z.string(),
  name: z.string(),
  color: z.string(),
});
export type Tag = z.infer<typeof Tag>;

export const ChecklistItem = z.object({
  id: z.string(),
  text: z.string(),
  done: z.boolean(),
  position: z.number(),
  // Display names, same codec as a task's own assignees/participants
  // (server/owners.js) — an item can have more than one.
  assignees: z.array(z.string()).default([]),
  // '' means unset, matching finish_date/start_date's own convention rather
  // than null — so a bare `||` fallback already does the right thing anywhere
  // this is read.
  due_date: z.string().default(''),
});

export const Attachment = z.object({
  id: z.string(),
  original_name: z.string(),
  size: z.number(),
  uploaded_at: z.number(),
});

/**
 * One side of a note's reactions.
 *
 * **Names as well as a count**, because the question a note's author is asking
 * is *who* has acknowledged it — an anonymous "3 👍" cannot answer that, which
 * defeats the point of a control whose whole purpose is acknowledgement.
 */
export const ReactionSide = z.object({
  count: z.number().default(0),
  names: z.array(z.string()).default([]),
});

export const Reactions = z.object({
  up: ReactionSide.default({ count: 0, names: [] }),
  down: ReactionSide.default({ count: 0, names: [] }),
  /**
   * What *this* reader has on the note, or null.
   *
   * Computed on the server rather than derived in a component: "did I react"
   * has one answer, and two components working it out from `names` would be
   * two chances to disagree — names are display names and can repeat.
   */
  mine: z.enum(['up', 'down']).nullable().default(null),
});

export const Message = z.object({
  id: z.string(),
  author: z.string(),
  text: z.string(),
  created_at: z.number(),
  isRead: z.boolean(),
  // Defaulted, so a payload written by an older server still parses rather
  // than blanking the whole thread on the first deploy.
  reactions: Reactions.default({ up: { count: 0, names: [] }, down: { count: 0, names: [] }, mine: null }),
});

export type ReactionKind = 'up' | 'down';
/** The inferred shape, for components that take it as a prop. */
export type ReactionSummary = z.infer<typeof Reactions>;

export const CircleRef = z.object({
  id: z.string(),
  name: z.string(),
  color: z.string(),
});

export const Pin = z.object({
  circleId: z.string(),
  name: z.string(),
  color: z.string(),
  pinnedBy: z.string().nullable().optional(),
  canUnpin: z.boolean(),
});

export const Task = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullable().default(''),
  /**
   * The column this card is in **on the board being looked at**.
   *
   * Was `z.enum` of the five ids; a circle can create its own now, so the check
   * that this used to give has moved to where it can still be made — the server
   * validates against the circle's own columns, and the board only draws ids it
   * has a column for.
   *
   * For a task pinned in from another circle this may differ from where it sits
   * at home; `homeVertical` says so when it does.
   */
  vertical: z.string(),
  /**
   * The column this task is in **at home**, and only when that differs from
   * `vertical` above — otherwise null, so an ordinary card carries nothing
   * extra. It is what the "Core · WIP" chip on a borrowed card reads.
   */
  homeVertical: z.string().nullable().default(null),
  homeVerticalLabel: z.string().default(''),
  start_date: z.string().nullable().default(''),
  finish_date: z.string().nullable().default(''),
  position: z.number(),
  priority: z.number().nullable().default(DEFAULT_PRIORITY),
  sowNumber: z.string().default(''),

  created_at: z.number(),
  updated_at: z.number(),
  completed_at: z.number().nullable().default(0),
  created_by_name: z.string().nullable().default(''),
  created_by_email: z.string().nullable().default(''),

  archived: z.boolean(),
  agedOut: z.boolean(),
  retentionDays: z.number().optional(),

  /**
   * **Two roles, disjoint by construction.** Assignees are responsible for the
   * work; participants are kept informed. The server keeps a name out of both
   * (`separatePeople` in `server/owners.js`), so a component may render each list
   * without checking the other for duplicates.
   *
   * Notifications go to everybody in either — the split says who is answerable,
   * not who hears.
   */
  assignees: z.array(Owner).default([]),
  participants: z.array(Owner).default([]),
  tags: z.array(Tag).default([]),
  /**
   * **The arrays arrive from `GET /api/tasks/:id` and not from the board.**
   *
   * The board and the list read these only for their lengths, and the modal
   * fetches its own task anyway — so the list response carries the counts below
   * instead of 78 checklist rows and 38 message bodies that would be decrypted
   * on every twenty-second poll. See docs/mysql-migration/queries.md.
   *
   * **Optional, and deliberately not `.default([])`.** They used to default, and
   * that is exactly how the modal came to show "No checklist items yet" over a
   * task with eleven of them: it opens instantly on the board's copy of the task
   * (`initialData` in queries/task.ts), the default turned "not sent" into
   * "empty", and the draft seeded from that never re-seeded once the real list
   * arrived. Pressing Save then sent `checklist: []`, and the server replaces
   * the whole list — eleven rows gone, from a screen nobody had typed in.
   *
   * `undefined` says "not loaded" and an empty array says "there is nothing
   * there", which is the distinction the server has always made and the client
   * had thrown away. Read them as `?? []` only where that difference genuinely
   * does not matter.
   */
  checklist: z.array(ChecklistItem).optional(),
  attachments: z.array(Attachment).optional(),
  messages: z.array(Message).optional(),
  /** Always sent, by both shapes. Cards and rows must read these, not `.length`. */
  checklistTotal: z.number().default(0),
  checklistDone: z.number().default(0),
  attachmentCount: z.number().default(0),
  messageCount: z.number().default(0),
  unread: z.number().default(0),
  /** Changed since this person last opened it. Drives the dot on the card. */
  unseen: z.boolean().default(false),
  onTodo: z.boolean().default(false),

  /** The task's *home* circle. Sent in every view so a card always says where it lives. */
  circle: CircleRef.nullable(),
  circle_id: z.string().nullable().optional(),
  pins: z.array(Pin).default([]),
  pinnedElsewhere: z.boolean().default(false),
  pinCount: z.number().default(0),
  /** False for a borrowing circle: it may edit everything except the title, the
   *  description, archiving and deletion. */
  canEditFully: z.boolean().default(true),
  pinnedHere: z.boolean().default(false),
});
export type Task = z.infer<typeof Task>;

export const TaskList = z.array(Task);

/**
 * Overdue is a *display* rule, not a stored one, and it deliberately ignores
 * completed work: a finished task with a date in the past is finished, not late.
 */
export function isOverdue(task: Pick<Task, 'finish_date' | 'vertical'>): boolean {
  // `DONE_VERTICAL`, not a fifth literal spelling of it: the done column's id is
  // the same in every circle by construction, and naming the constant is what
  // keeps that true here.
  if (!task.finish_date || task.vertical === DONE_VERTICAL) return false;
  const today = new Date();
  const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  return task.finish_date < iso;
}
